/* eslint-disable tsdoc/syntax */
import {
  BeneficiariesIcon,
  DashboardIcon,
  InventoryIcon,
  Logo,
  LogoZ,
  OutletIcon,
  VirtualAccountIcon,
  OutlineArrowLeft,
  OutlineArrowRight,
  SettingsIcon,
  TransactionsIcon,
  TransferIcon,
  ClipboardIcon,
} from "./assets/NavigationIcons/_index";
import SidebarBackground from "./assets/SidebarBackground.svg";
import SidebarShortenBackground from "./assets/SidebarShortenBackground.svg";
import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { usePermissions } from "@zenith/features/AuthZ/usePermissions";
import { permissionsForPath } from "@zenith/features/AuthZ/permissionsForPath";
import { useAppDispatch, useAppSelector } from "@zenith/app/hooks";
import ChevronDown from "@zenith/assets/icons/ChevronDown";
import ChevronUp from "@zenith/assets/icons/ChevronUp";
import Logout from "@zenith/assets/icons/Logout";
import ProfileCircle from "@zenith/assets/icons/ProfileCircle";
import Star from "@zenith/assets/icons/Star";
import { ButtonComponent, ModalComponent } from "@zenith/components";
import OutletSwitcherComponent from "@zenith/features/OutletManagement/components/OutletSwitcherComponent/outlet-switcher-component/outlet-switcher-component";
import { useKeepOutletsLoaded } from "@zenith/features/OutletManagement/hooks/useKeepOutletsLoaded";
import { useOutletView } from "@zenith/features/OutletManagement/hooks/useOutletView";
import { useSwitchOutlet } from "@zenith/features/OutletManagement/hooks/useSwitchOutlet";
import { requiresMainOutlet } from "@zenith/features/OutletManagement/utils/main-outlet-paths";
import DiscardModalComponent from "@zenith/components/ui/DiscardModalComponent/discard-modal-component/discard-modal-component";
import { useOutletContext, useSendMoneyContext } from "@zenith/context";
import {
  useGlobalContextActions,
  usePaymentLinkContext,
  usePaymentsContext,
  useRegistrationContext,
  useUserDetailsContext,
} from "@zenith/context/global-context/hooks";
import {
  DISCARD_PAYMENT_BODY,
  DISCARD_PAYMENT_PRIMARY_BTN_TEXT,
  DISCARD_PAYMENT_SECONDARY_BTN_TEXT,
  DISCARD_PAYMENT_TITLE,
  DISCARD_TRANSFER_BODY,
  DISCARD_TRANSFER_BUTTON,
  DISCARD_TRANSFER_TITLE,
  IS_OUTLET_MANAGEMENT_ENABLED,
  RETURN_TO_TRANSFER_BUTTON,
  SUCCESS_API_CODE,
} from "@zenith/data/constants";
import { HandleLogout } from "@zenith/features/Authentication/utils/handleLogout";
import {
  CompleteRegistrationModal,
  useFinishRegistrationModal,
} from "@zenith/features/Dashboard";
import {
  emptyCart,
  selectCart,
} from "@zenith/features/Payments/components/ItemDetailsForm/item-details-form-component/cartSlice";
import { AppNavigation } from "@zenith/hooks";
import {
  ANALYTICS,
  BENEFICIARIES,
  VIRTUAL_ACCOUNTS,
  COLLECT_PAYMENT,
  DASHBOARD,
  INVENTORY,
  OUTLET_MANAGEMENT,
  PAYMENT,
  PAYMENT_BANK_TRANSFER,
  PAYMENT_OVERVIEW,
  PAYMENT_QR_CODE,
  PAYMENT_USSD_CODE,
  PROFILE_MANAGEMENT,
  SEND_MONEY,
  SEND_MONEY_OVERVIEW,
  SEND_MONEY_PIN,
  SETTINGS,
  STATEMENTS,
  TRANSACTIONS,
  UPGRADE_ACCOUNT,
  POS_MANAGEMENT,
} from "@zenith/routes/paths";
import {
  ApiResponseDTOUserDetailDTO,
  ApiResponseDTOWalletBalanceInfoResponseDTO,
  PreferencesApiService,
  PreferencesDTO,
  UsersApiService,
  WalletApiService,
} from "@zenith/services/user";
import { SidebarProps, UserRole } from "@zenith/types";
import {
  cleanLocalStorageInfo,
  getBeneficiaryInitials,
  getPageTitle,
} from "@zenith/utils";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import useVirtualAccountFeatures from "@zenith/hooks/useVirtualAccountFeatures";
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import AnalyticsIcon from "./assets/NavigationIcons/Analytics";
import StatementsIcon from "./assets/NavigationIcons/Statements";
import CollectPaymentIcon from "./assets/NavigationIcons/CollectPayment";
import { SetShowSideBar } from "@zenith/features/LandingPage";
import MenuIcon from "@mui/icons-material/Menu";
import Close from "@mui/icons-material/Close";
import { ApiResponseUserSettingsPreference } from "../interfaces";
import WalletIcon from "@zenith/assets/icons/WalletIcon";

/* eslint-disable-next-line */
export interface MainLayoutProps {
  sidebar?: (props: SidebarProps) => JSX.Element;
}

const MainLayout = memo(function MainLayout(props: MainLayoutProps) {
  const { clearUnusedContexts } = useGlobalContextActions();
  const { sendMoneyContext, setSendMoneyContext, clearSendMoneyContext } =
    useSendMoneyContext();
  const {
    registrationContext: { isRegistrationCompleted, isBusinessSectorCompleted },
    setRegistrationContext,
  } = useRegistrationContext();
  const { openFinishRegistrationModal, finishRegistrationModal } =
    useFinishRegistrationModal();
  const { userDetailsContext, setUserDetailsContext } = useUserDetailsContext();
  const { role, firstName, id, isLoadingUser } = userDetailsContext;
  const { isModuleEnabled: isVirtualAccountModuleEnabled } =
    useVirtualAccountFeatures();

  const { handleLogout, loggingOut } = HandleLogout();
  const { navigateTo } = AppNavigation();
  const location = useLocation();
  const cart = useAppSelector(selectCart);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isEmployee = role === UserRole.EMPLOYEE;
  // BOAS-7641 Scenario 5: only the menu the role may use is rendered.
  const { can, businessType, role: profileRole, resolved } = usePermissions();
  // The role that decides access is the one the user is *in*, not the claim on
  // the token: the token is minted for the profile used at sign-in, so a
  // merchant who then chooses their Team Member profile still carries an
  // ADMINISTRATOR claim and would keep the owner's menu.
  const isAdmin = profileRole === "admin";

  // Hiding the menu item is not access control: a URL typed by hand must be
  // refused too. The role starts at its least-privileged value, so the guard
  // waits for the profile before it refuses anything - otherwise the owner is
  // bounced off their own page on the first render.
  const requiredPermissions = permissionsForPath(location.pathname);
  useEffect(() => {
    if (!resolved) return;
    if (requiredPermissions.length === 0) return;
    if (requiredPermissions.some((permission) => can(permission))) return;
    navigate(DASHBOARD, { replace: true });
  }, [location.pathname, requiredPermissions, can, navigate, resolved]);
  useKeepOutletsLoaded();
  const {
    isMainOutletInView,
    isSingleOutletView,
    mainOutlet,
    isLoadingOutlets,
    outlets,
  } = useOutletView();
  const { setOutletSelection } = useOutletContext();
  const switchOutlet = useSwitchOutlet();

  // Covers a typed URL, a refresh or Back onto a main-outlet page while another outlet is in view.
  const isMainOutletPath = requiresMainOutlet(location.pathname);
  useEffect(() => {
    if (isLoadingOutlets || !isMainOutletPath || isMainOutletInView) return;
    clearSendMoneyContext();
    navigate(DASHBOARD, { replace: true });
  }, [isLoadingOutlets, isMainOutletPath, isMainOutletInView, clearSendMoneyContext, navigate]);

  // The wallet is the signed-in outlet's, so re-read it whenever that outlet changes.
  const currentOutletId = outlets.find((outlet) => outlet.isCurrent)?.id;
  const latestWalletRequest = useRef(0);
  useEffect(() => {
    if (!currentOutletId) return;
    const request = ++latestWalletRequest.current;
    WalletApiService.getWalletDetails()
      .then((response: ApiResponseDTOWalletBalanceInfoResponseDTO) => {
        if (request !== latestWalletRequest.current) return;
        if ("data" in response && response.data) {
          setUserDetailsContext({ walletId: response.data.walletId as string });
        }
      })
      .catch((error) =>
        console.error("Error fetching wallet details:", error.message)
      );
  }, [currentOutletId]);

  // Get contexts for polling
  const { paymentLinkContext, setPaymentLinkContext } = usePaymentLinkContext();
  const { paymentsContext, setPaymentsContext, clearPaymentsContext } =
    usePaymentsContext();

  const [isTooltip, setTooltip] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [isSidebarShortened, setSidebarShortened] = useState<boolean>(false);
  const [isHomeTooltipOpen, setIsHomeTooltipOpen] = useState<boolean>(false);
  const [isCollectPaymentTooltipOpen, setIsCollectPaymentTooltipOpen] =
    useState<boolean>(false);
  const [isTransactionsTooltipOpen, setIsTransactionsTooltipOpen] =
    useState<boolean>(false);
  const [isSendMoneyTooltipOpen, setIsSendMoneyTooltipOpen] =
    useState<boolean>(false);
  const [isPosManagementTooltipOpen, setIsPosManagementTooltipOpen] =
    useState<boolean>(false);
  const [isBeneficiariesTooltipOpen, setIsBeneficiariesTooltipOpen] =
    useState<boolean>(false);
  const [isVirtualAccountTooltipOpen, setIsVirtualAccountTooltipOpen] =
    useState<boolean>(false);
  const [isOutletManagementTooltipOpen, setIsOutletManagementTooltipOpen] =
    useState<boolean>(false);
  const [isAnalyticsTooltipOpen, setIsAnalyticsTooltipOpen] =
    useState<boolean>(false);
  const [isSettingsTooltipOpen, setIsSettingsTooltipOpen] =
    useState<boolean>(false);
  const [isStatementsTooltipOpen, setIsStatementsTooltipOpen] =
    useState<boolean>(false);
  const [isInventoryTooltipOpen, setIsInventoryTooltipOpen] =
    useState<boolean>(false);
  const [logoutModal, setLogoutModal] = useState<boolean>(false);
  const [isOpenPaymentModal, setIsOpenPaymentModal] = useState<boolean>(false);
  const [isOpenTransferModal, setIsOpenTransferModal] =
    useState<boolean>(false);
  const [selectedPath, setSelectedPath] = useState<string>(location.pathname);
  const [showRegistrationModal, setShowRegistrationModal] =
    useState<boolean>(false);
  const [tierType, setTierType] = useState<string>("");
  const [isWalletLoaded, setIsWalletLoaded] = useState<boolean>(false);
  const [walletId, setWalletId] = useState<string>("");
  const [wallet, setWallet] = useState<{
    availableBalance: number | null;
    pendingBalance: number | null;
    ledgerBalance: number | null;
    ledgerBalanceTooltip: string | "";
  }>({
    availableBalance: null,
    pendingBalance: null,
    ledgerBalance: null,
    ledgerBalanceTooltip: "",
  });
  const [tierStatus, setTierStatus] = useState({
    tierUpgradeStatus: false,
  });
  const [userSettingsPreference, setUserSettingsPreference] =
    useState<PreferencesDTO>();

  const isResized = useCallback((): boolean => {
    return window.innerWidth < 1024 && window.innerWidth > 639;
  }, []);
  const { showSideBar, onSetShowSideBar, onSetButtonEffect, effect } =
    SetShowSideBar();

  const isCorporate = tierType === "CORPORATE";
  // Corporate accounts can't send money or manage beneficiaries
  const isCorporateRestrictedPath = [SEND_MONEY, BENEFICIARIES].some((path) =>
    location.pathname.startsWith(path)
  );
  const sidebarTooltipProps = {
      componentsProps: {
        tooltip: {
          sx: {
            backgroundColor: "#373737",
            borderRadius: "8px",
            fontSize: "14px",
          },
        },
        arrow: {
          sx: {
            color: "#373737",
          },
        },
      },
    },
    openPaymentModalRoutes = [
      PAYMENT,
      PAYMENT_OVERVIEW,
      PAYMENT_USSD_CODE,
      PAYMENT_BANK_TRANSFER,
      PAYMENT_QR_CODE,
    ],
    openTransferModalRoutes = [SEND_MONEY, SEND_MONEY_OVERVIEW, SEND_MONEY_PIN];

  function handleToggleSidebar(): void {
    setSidebarShortened(!isSidebarShortened);
  }

  function handleClick(event: React.MouseEvent<HTMLButtonElement>): void {
    setAnchorEl(event.currentTarget);
    setIsDropdownOpen(true);
  }

  function handleClose(): void {
    setAnchorEl(null);
    setIsDropdownOpen(false);
  }

  function openLogoutModal(): void {
    setLogoutModal(true);
    return;
  }

  function closeLogoutModal(): void {
    setLogoutModal(false);
  }

  function handleProfileManagement(): void {
    navigateTo(PROFILE_MANAGEMENT);
    setAnchorEl(null);
    setIsDropdownOpen(false);
  }

  function openPaymentModal(): void {
    setIsOpenPaymentModal(true);
  }

  function closePaymentModal(): void {
    setIsOpenPaymentModal(false);
  }

  function openTransferModal(): void {
    setIsOpenTransferModal(true);
  }

  function closeTransferModal(): void {
    setIsOpenTransferModal(false);
  }

  function clearCart(): void {
    dispatch(emptyCart());
  }

  function checkTransferFieldsFilled(): boolean {
    return [
      sendMoneyContext.amount,
      sendMoneyContext.bankId,
      sendMoneyContext.beneficiaryAccountNumber,
      sendMoneyContext.beneficiaryName,
      sendMoneyContext.categoryId,
      sendMoneyContext.description,
    ].some((value) => !!value);
  }

  function updatePaymentMethod(): void {
    setPaymentsContext({
      ...paymentsContext,
      paymentMethod: undefined,
    });
  }

  function handleUpgradeAccountTier(): void {
    navigateTo(UPGRADE_ACCOUNT);
  }

  function handleNavLinkClick(
    event: React.MouseEvent<HTMLAnchorElement>,
    route?: string
  ): void {
    if (!route) return;

    setSelectedPath(route);

    if (openPaymentModalRoutes.includes(location.pathname) && cart.length > 0) {
      event.preventDefault();
      return openPaymentModal();
    } else if (
      openTransferModalRoutes.includes(location.pathname) &&
      checkTransferFieldsFilled()
    ) {
      event.preventDefault();
      return openTransferModal();
    } else {
      setTooltip(false);
      setTimeout(() => {
        navigateTo(route);
        setTooltip(true);
      }, 0);
      clearData();
    }

    !props.sidebar && clearUnusedContexts();
  }

  function clearContext(): void {
    clearPaymentsContext();
    cleanLocalStorageInfo();
  }

  function clearData(): void {
    clearCart();
    clearContext();
    closePaymentModal();
    updatePaymentMethod();
  }

  function clearTransferFields(): void {
    setSendMoneyContext({
      ...sendMoneyContext,
      amount: Number(""),
      bankName: "",
      bankId: "",
      beneficiaryAccountNumber: "",
      beneficiaryName: "",
      categoryId: "",
      categoryName: "",
      description: "",
      saveBeneficiaryStatus: undefined,
    });
  }

  function handleEmptyCart(): void {
    clearData();
    navigate(selectedPath);
  }

  function handleEmptyTransfer(): void {
    clearTransferFields();
    closeTransferModal();
    navigate(selectedPath);
  }

  function getTierColor(tierType: string): string {
    switch (tierType) {
      case "BRONZE":
        return "#5656CA";
      case "SILVER":
        return "#F16613";
      case "GOLD":
        return "#F0B02A";
      case "PLATINUM":
        return "#6EBD7F";
      default:
        return "#000000";
    }
  }

  const tierColor = getTierColor(tierType);

  useEffect(() => {
    if (
      !!isRegistrationCompleted ||
      !isBusinessSectorCompleted ||
      !userDetailsContext.email ||
      !isLoadingUser
    ) {
      setShowRegistrationModal(true);
    }
  }, []);

  const getUserSettingsPreference = async () => {
    try {
      if (id) {
        const response = (await PreferencesApiService.getPreferences(
          id
        )) as ApiResponseUserSettingsPreference;
        if (
          response.internalCode === SUCCESS_API_CODE &&
          response.success &&
          response.data
        ) {
          const data = response.data;
          setUserDetailsContext({
            userSettingsPreference: data,
          });
          setUserSettingsPreference(data as PreferencesDTO);
        }
      }
    } catch (error: any) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    getUserSettingsPreference();
  }, [id]);

  useEffect(() => {
    if (!walletId) {
      WalletApiService.getWalletDetails()
        .then((response: ApiResponseDTOWalletBalanceInfoResponseDTO) => {
          if ("data" in response && response.data) {
            setTierType(response.data.tierType as string);
            setWalletId(response.data.externalAccountNumber as string);
            setTierStatus({
              tierUpgradeStatus: response.data.isUpgradeTierEnabled as boolean,
            });
            setWallet({
              availableBalance: response.data.balance
                ?.availableBalance as number,
              pendingBalance: response.data.balance?.pendingBalance as number,
              ledgerBalance: response.data.balance?.ledgerBalance as number,
              ledgerBalanceTooltip: response.data.balance
                ?.ledgerBalanceTooltip as string,
            });
            const isRegistrationCompleted = response.data
              .isRegistrationCompleted as boolean;
            const isBusinessSectorCompleted = response.data
              .isBusinessSectorCompleted as boolean;
            setRegistrationContext({
              isRegistrationCompleted,
              isBusinessSectorCompleted,
            });
            setUserDetailsContext({
              isBalanceVisible: response.data.showWallet ?? true,
            });
            if (!isBusinessSectorCompleted || !isRegistrationCompleted) {
              setShowRegistrationModal(true);
            }
            setUserDetailsContext({
              availableBalance: response.data.balance
                ?.availableBalance as number,
              pendingBalance: response.data.balance?.pendingBalance as number,
              ledgerBalance: response.data.balance?.ledgerBalance as number,
              ledgerBalanceTooltip: response.data.balance
                ?.ledgerBalanceTooltip as string,
            });
            localStorage.setItem("userTier", String(response?.data.tierType));
          }
        })
        .catch((error) => {
          console.error("Error fetching wallet details:", error.message);
          setUserDetailsContext({ walletError: true });
        })
        .finally(() => setIsWalletLoaded(true));
    } else {
      // No fetch to wait on, so don't hold restricted pages back
      setIsWalletLoaded(true);
    }
    if (!firstName) {
      UsersApiService.getUser()
        .then((response: ApiResponseDTOUserDetailDTO) => {
          if ("data" in response && response.data) {
            setUserDetailsContext({
              availableBalance: wallet.availableBalance,
              pendingBalance: wallet.pendingBalance,
              ledgerBalance: wallet.ledgerBalance,
              ledgerBalanceTooltip: wallet?.ledgerBalanceTooltip as string,
              bankVerificationNumber:
                response.data.bankVerificationNumber ?? "",
              businessName: response.data.businessName ?? "",
              countryCode: response.data.countryCode ?? "",
              email: response.data.email ?? "",
              firstName: response.data.firstName ?? "",
              lastName: response.data.lastName ?? "",
              phoneNumber: response.data.phoneNumber ?? "",
              physicalBusinessAddress:
                response.data.physicalBusinessAddress ?? "",
              tradingName: response.data.tradingName ?? "",
              birthdate: response.data.birthdate ?? "",
              state: "SUCCESS",
              isLoadingUser: Boolean(response.data.email),
            });
          }
        })
        .catch((error) => {
          console.error(error.message);
          setUserDetailsContext({
            state: "ERROR",
          });
        });
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setSidebarShortened(isResized());

      if (window.innerWidth >= 630) {
        onSetShowSideBar(false);
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [isResized]);

  const userName = `${userDetailsContext.firstName} ${userDetailsContext.lastName}`;
  const businessTradingName =
    userDetailsContext.businessName ||
    userDetailsContext.tradingName ||
    userName.trim();
  // A team member sees their own name in whichever business they are in; the
  // sign-in profile only stands in until the current one loads.
  const showsOwnName = resolved ? !isAdmin : isEmployee;
  const headerName = showsOwnName ? userName : businessTradingName || userName;
  return (
    <div
      className={` mt-32 w-full overflow-x-clip px-3 sm:px-0 ${
        showSideBar ? "h-screen !overflow-y-hidden overscroll-y-none" : ""
      } `}
    >
      <div>
        {finishRegistrationModal}
        {showRegistrationModal && <CompleteRegistrationModal />}

        {isOpenPaymentModal && (
          <DiscardModalComponent
            isOpen={isOpenPaymentModal}
            onClose={closePaymentModal}
            primaryAction={handleEmptyCart}
            secondaryAction={closePaymentModal}
            title={DISCARD_PAYMENT_TITLE}
            bodyText={DISCARD_PAYMENT_BODY}
            primaryActionText={DISCARD_PAYMENT_PRIMARY_BTN_TEXT}
            secondaryActionText={DISCARD_PAYMENT_SECONDARY_BTN_TEXT}
          />
        )}
        {isOpenTransferModal && (
          <DiscardModalComponent
            isOpen={isOpenTransferModal}
            onClose={closeTransferModal}
            primaryAction={handleEmptyTransfer}
            secondaryAction={closeTransferModal}
            title={DISCARD_TRANSFER_TITLE}
            bodyText={DISCARD_TRANSFER_BODY}
            primaryActionText={DISCARD_TRANSFER_BUTTON}
            secondaryActionText={RETURN_TO_TRANSFER_BUTTON}
          />
        )}
        <header
          className={` fixed inset-x-0 top-0 z-50 flex h-28 w-full flex-wrap  bg-white pt-6 text-sm sm:flex-nowrap sm:justify-start md:justify-end ${
            isResized() ? "lg:pl-20" : "lg:pl-44"
          }`}
        >
          <nav
            className="ml-4 flex w-full items-center justify-between py-6 pr-4 pl-0 sm:ml-0 sm:pl-28"
            aria-label="Global"
          >
            <div className="flex gap-2">
              <div className="my-auto block sm:hidden">
                <button
                  id="side-menu-button"
                  daisresizedta-drawer-target="default-sidebar"
                  data-drawer-toggle="default-sidebar"
                  aria-controls="default-sidebar"
                  type="button"
                  onClick={() => {
                    onSetButtonEffect(true);
                    onSetShowSideBar(true);
                  }}
                  onAnimationEnd={() => {
                    onSetButtonEffect(false);
                  }}
                  className={`${
                    effect && "animate-[pulse_1s_1]"
                  } focus:outline-0`}
                >
                  <MenuIcon fontSize="medium" className="my-auto" />
                </button>
              </div>
              <h1 className="text-sm sm:text-2xl">
                {(location.state as { pageTitle?: string } | null)?.pageTitle ||
                  getPageTitle(location.pathname)}
              </h1>
            </div>
            <div>
              <div className="flex flex-row items-center">
                {!isEmployee && tierStatus.tierUpgradeStatus && (
                  <div className="hidden md:flex">
                    <ButtonComponent
                      label="Upgrade"
                      onClick={handleUpgradeAccountTier}
                      id="upgrade-button"
                      iconStart={<Star fill="white" />}
                      size="large"
                      type="primary"
                    />
                    <div className="mx-8 hidden h-[50px]  w-0 border-r border-neutral-100 md:block"></div>
                  </div>
                )}

                {/* Outlets belong to the business owner - and, per BOAS-7641,
                    to Admin, who "performs all functions as the owner" and
                    is "not tied to an outlet". isEmployee is the sign-in
                    claim, which is EMPLOYEE for an Admin team member too, so
                    it alone would wrongly hide this from them; isAdmin comes
                    from the resolved permission profile instead. */}
                {IS_OUTLET_MANAGEMENT_ENABLED && (!isEmployee || isAdmin) && (
                  <OutletSwitcherComponent
                    defaultOutletName={businessTradingName}
                  />
                )}

                <div
                  className="mx-4 flex items-center rounded-full border-3"
                  style={{ borderColor: tierColor }}
                >
                  <div className=" flex h-7 w-7 items-center justify-center rounded-full bg-primary-300/25 text-sm text-primary-300 sm:h-9 sm:w-9 sm:text-base">
                    {getBeneficiaryInitials(headerName)?.toUpperCase()}
                  </div>
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-xs text-neutral-900 sm:text-sm md:text-base">
                    {headerName}
                  </span>
                  <span
                    className="text-right text-xs font-medium capitalize"
                    style={{ color: tierColor }}
                  >
                    {tierType.toLowerCase()}
                  </span>
                </div>
                <div className="mx-1 flex items-center md:mx-4">
                  <IconButton onClick={handleClick}>
                    {isDropdownOpen ? (
                      <ChevronUp color={"#1A1C1F"} />
                    ) : (
                      <ChevronDown color={"#1A1C1F"} />
                    )}
                  </IconButton>
                  <Menu
                    id="dashboard-dropdown"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    className="mt-12"
                    sx={{
                      "& .MuiPaper-root.MuiPopover-paper.MuiMenu-paper": {
                        borderRadius: "10px",
                      },
                    }}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                  >
                    <MenuItem
                      onClick={handleProfileManagement}
                      className="mx-2 mt-1 rounded-lg px-2 pb-2 text-sm text-neutral-600"
                    >
                      <ProfileCircle className="mr-2 w-5" /> Profile Management
                    </MenuItem>
                    <MenuItem
                      onClick={openLogoutModal}
                      className="mx-2 my-1 rounded-lg px-2 pt-2 text-sm text-neutral-600"
                    >
                      <Logout className="mr-2 w-5" /> Logout
                    </MenuItem>
                    {logoutModal && (
                      <ModalComponent
                        body="Are you sure you want to end the session?"
                        title="Logout"
                        showErrorIcon={false}
                        showModal={logoutModal}
                        onClickClose={closeLogoutModal}
                        doubleOptions={true}
                        onLeftButtonClick={closeLogoutModal}
                        onRightButtonClick={handleLogout}
                        leftButtonText="Cancel"
                        isRightLoading={loggingOut}
                        rightButtonText="Logout"
                      />
                    )}
                  </Menu>
                </div>
              </div>
            </div>
          </nav>
        </header>
        <div
          className={` ${isSidebarShortened ? "w-20 px-3" : "w-64 px-6"}
          hs-overlay hs-overlay-open:translate-x-0 no-scrollbar realtive top-0 left-0 bottom-0 z-[60] h-full transform  overflow-y-auto border-r border-gray-200 py-4 transition-all duration-75 sm:fixed sm:block lg:right-auto lg:bottom-0 lg:block lg:translate-x-0 min-h-601:py-8
          ${isResized() ? "overflow-y-auto" : ""}
          ${showSideBar ? "fixed z-[70] max-h-screen w-full" : "hidden"}
          `}
          style={{
            backgroundImage: isSidebarShortened
              ? `url(${SidebarShortenBackground})`
              : `url(${SidebarBackground})`,
          }}
        >
          {!props.sidebar ? (
            <aside
              id="sidebar"
              className="flex flex-col justify-between overflow-y-hidden"
            >
              <div
                className={`pt-0 ${
                  isSidebarShortened
                    ? "mx-1 mb-2 min-h-601:mb-8"
                    : "min-h-6 mb-4 flex w-full place-items-start sm:place-items-center"
                }`}
              >
                {isSidebarShortened ? (
                  <LogoZ />
                ) : (
                  <div className="mt-5 flex w-full justify-between sm:mt-0 sm:block sm:justify-center">
                    <div className="flex gap-2">
                      <WalletIcon
                        fill="white"
                        height={40}
                        width={40}
                        className="my-auto"
                      />
                      <Logo className={`${showSideBar ? "" : "mx-auto"}`} />
                    </div>
                    <IconButton
                      className="block text-white hover:bg-inherit sm:hidden"
                      onClick={() => onSetShowSideBar(false)}
                    >
                      <Close />
                    </IconButton>
                  </div>
                )}
              </div>
              <ul className="mt-3 flex w-2/3 select-none  list-none flex-col place-items-center gap-1.5 sm:mt-0 sm:w-full min-h-601:gap-3">
                <div className="mb-3 w-full">
                  {!isEmployee && tierStatus.tierUpgradeStatus && (
                    <div className="block w-3/5 sm:hidden">
                      <ButtonComponent
                        label="Upgrade"
                        onClick={handleUpgradeAccountTier}
                        id="upgrade-button"
                        iconStart={<Star fill="#da291c" />}
                        type=""
                        size="large"
                        className="w-full bg-white text-primary-300"
                      />
                      <div className="mx-8 hidden h-[50px]  w-0 border-r border-neutral-100 md:block"></div>
                    </div>
                  )}
                </div>
                {/* BOAS-7641: Home (a scoped balance view) is in every role's
                    table, not just the owner's. */}
                <li className="h-12 w-full">
                  <NavLink
                    id="dashboard-link"
                    to="#"
                    className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(DASHBOARD)
                        ? "bg-white fill-white stroke-primary-300 text-primary-300 [&>span]:duration-300"
                        : "bg-neutral-transparent-white fill-neutral-transparent-white stroke-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200 hover:fill-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                    onClick={(e) => {
                      handleNavLinkClick(e, DASHBOARD);
                      onSetShowSideBar(false);
                    }}
                    onMouseEnter={() => setIsHomeTooltipOpen(true)}
                    onMouseLeave={() => setIsHomeTooltipOpen(false)}
                  >
                    <DashboardIcon
                      className={`py-auto h-full w-5 ${
                        isSidebarShortened ? "col-span-1 ml-5.5" : "mr-2.5 ml-3"
                      }`}
                    />
                    {isSidebarShortened && isTooltip && (
                      <Tooltip
                        {...sidebarTooltipProps}
                        title="Home"
                        placement="right"
                        arrow
                        disableInteractive={true}
                        open={isHomeTooltipOpen}
                        className="px-3 py-0 "
                      >
                        <div></div>
                      </Tooltip>
                    )}
                    <span
                      className={`flex h-full items-center ${
                        isSidebarShortened ? "hidden" : ""
                      }`}
                    >
                      Home
                    </span>
                  </NavLink>
                </li>
                {/* Cashier-only: the story grants "Collect Payment" to Cashier
                    and never to Manager or Accountant. */}
                {can("payment:collect") && (
                  <li className="h-12 w-full">
                    <NavLink
                      id="collect-payment-link"
                      to={COLLECT_PAYMENT}
                      className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(COLLECT_PAYMENT)
                        ? "bg-white fill-white stroke-primary-300 text-primary-300 [&>span]:duration-300"
                        : "bg-neutral-transparent-white fill-neutral-transparent-white stroke-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200 hover:fill-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                      onClick={(e) => {
                        handleNavLinkClick(e, COLLECT_PAYMENT);
                        onSetShowSideBar(false);
                      }}
                      onMouseEnter={() => setIsCollectPaymentTooltipOpen(true)}
                      onMouseLeave={() => setIsCollectPaymentTooltipOpen(false)}
                    >
                      <CollectPaymentIcon
                        className={`py-auto h-full w-5 ${
                          isSidebarShortened
                            ? "col-span-1 ml-5.5"
                            : "mr-2.5 ml-3"
                        }`}
                      />
                      {isSidebarShortened && isTooltip && (
                        <Tooltip
                          {...sidebarTooltipProps}
                          title="Collect Payment"
                          placement="right"
                          arrow
                          disableInteractive={true}
                          open={isCollectPaymentTooltipOpen}
                          className="px-3 py-0"
                        >
                          <div></div>
                        </Tooltip>
                      )}
                      <span
                        className={`flex h-full items-center ${
                          isSidebarShortened ? "hidden" : ""
                        }`}
                      >
                        Collect Payment
                      </span>
                    </NavLink>
                  </li>
                )}
                <li className="h-12 w-full">
                  <NavLink
                    id="transactions-link"
                    to="#"
                    className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(TRANSACTIONS)
                        ? "bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : "bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                    onClick={(e) => {
                      if (!isRegistrationCompleted) {
                        openFinishRegistrationModal();
                      } else {
                        handleNavLinkClick(
                          e,
                          isRegistrationCompleted
                            ? TRANSACTIONS
                            : location.pathname
                        );
                      }
                      onSetShowSideBar(false);
                    }}
                    onMouseEnter={() => setIsTransactionsTooltipOpen(true)}
                    onMouseLeave={() => setIsTransactionsTooltipOpen(false)}
                  >
                    <TransactionsIcon
                      className={` h-full w-5 ${
                        isSidebarShortened ? "col-span-1 ml-5.5" : "mr-2.5 ml-3"
                      }`}
                    />
                    {isSidebarShortened && isTooltip && (
                      <Tooltip
                        {...sidebarTooltipProps}
                        title="Transactions"
                        placement="right"
                        arrow
                        disableInteractive={true}
                        open={isTransactionsTooltipOpen}
                        className="px-3 py-0"
                      >
                        <div></div>
                      </Tooltip>
                    )}
                    <span
                      className={`flex h-full items-center ${
                        isSidebarShortened ? "hidden" : ""
                      }`}
                    >
                      Transactions
                    </span>
                  </NavLink>
                </li>
                {/* Send money and beneficiaries both act on the main outlet's
                    wallet. BOAS-7641 also lets a multi-signatory Manager and
                    Accountant start a transfer there, subject to approval. */}
                {isMainOutletInView &&
                  !isCorporate &&
                  (isAdmin || can("transfer:initiate")) && (
                    <>
                      <li className="h-12 w-full">
                        <NavLink
                          id="send-money-link"
                          to="#"
                          className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(SEND_MONEY)
                        ? "bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : "bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : "pr-4"}`}
                          onClick={async (e) => {
                            if (!isRegistrationCompleted) {
                              openFinishRegistrationModal();
                            } else {
                              // The flow always acts on the main outlet's wallet, and
                              // the backend scopes by the session's outlet, so All
                              // Outlets has to switch to main before it opens.
                              if (!isSingleOutletView && mainOutlet?.id) {
                                try {
                                  await switchOutlet(mainOutlet);
                                  setOutletSelection({
                                    kind: "outlets",
                                    ids: [mainOutlet.id],
                                  });
                                } catch (error) {
                                  console.error(
                                    "Failed to switch to the main outlet:",
                                    error
                                  );
                                  return;
                                }
                              }
                              handleNavLinkClick(
                                e,
                                isRegistrationCompleted
                                  ? SEND_MONEY
                                  : location.pathname
                              );
                            }
                            onSetShowSideBar(false);
                          }}
                          onMouseEnter={() => setIsSendMoneyTooltipOpen(true)}
                          onMouseLeave={() => setIsSendMoneyTooltipOpen(false)}
                        >
                          <TransferIcon
                            className={` h-full w-5 ${
                              isSidebarShortened
                                ? "col-span-1 ml-5.5"
                                : "mr-3 ml-3"
                            }`}
                          />
                          {isSidebarShortened && isTooltip && (
                            <Tooltip
                              {...sidebarTooltipProps}
                              title="Send Money"
                              placement="right"
                              arrow
                              disableInteractive={true}
                              open={isSendMoneyTooltipOpen}
                              className="px-3 py-0"
                            >
                              <div></div>
                            </Tooltip>
                          )}
                          <span
                            className={`flex h-full items-center ${
                              isSidebarShortened ? "hidden" : ""
                            }`}
                          >
                            Send Money
                          </span>
                        </NavLink>
                      </li>
                      {/* VAAS is Manager's under Multi-signatory only - the
                        Individual table drops it from Manager's row too. */}
                      {isVirtualAccountModuleEnabled && can("vaas:manage") && (
                        <li className="h-12 w-full">
                          <NavLink
                            id="virtual-account-link"
                            to="#"
                            className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(VIRTUAL_ACCOUNTS)
                        ? "bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : "bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                            onClick={(e) => {
                              handleNavLinkClick(e, VIRTUAL_ACCOUNTS);
                              onSetShowSideBar(false);
                            }}
                            onMouseEnter={() =>
                              setIsVirtualAccountTooltipOpen(true)
                            }
                            onMouseLeave={() =>
                              setIsVirtualAccountTooltipOpen(false)
                            }
                          >
                            <VirtualAccountIcon
                              className={`h-full ${
                                isSidebarShortened
                                  ? "col-span-1 ml-5.5"
                                  : "mr-2 ml-3"
                              }`}
                            />
                            {isSidebarShortened && isTooltip && (
                              <Tooltip
                                {...sidebarTooltipProps}
                                title="Virtual Account"
                                placement="right"
                                arrow
                                disableInteractive={true}
                                open={isVirtualAccountTooltipOpen}
                                className="px-3 py-0"
                              >
                                <div></div>
                              </Tooltip>
                            )}
                            <span
                              className={`flex h-full items-center ${
                                isSidebarShortened ? "hidden" : ""
                              }`}
                            >
                              Virtual Account
                            </span>
                          </NavLink>
                        </li>
                      )}
                      <li className="h-12 w-full">
                        <NavLink
                          id="beneficiaries-link"
                          to="#"
                          className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(BENEFICIARIES)
                        ? "bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : "bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                          onClick={(e) => {
                            handleNavLinkClick(e, BENEFICIARIES);
                            onSetShowSideBar(false);
                          }}
                          onMouseEnter={() =>
                            setIsBeneficiariesTooltipOpen(true)
                          }
                          onMouseLeave={() =>
                            setIsBeneficiariesTooltipOpen(false)
                          }
                        >
                          <BeneficiariesIcon
                            className={`h-full ${
                              isSidebarShortened
                                ? "col-span-1 ml-5.5"
                                : "mr-2 ml-3"
                            }`}
                          />
                          {isSidebarShortened && isTooltip && (
                            <Tooltip
                              {...sidebarTooltipProps}
                              title="Beneficiaries"
                              placement="right"
                              arrow
                              disableInteractive={true}
                              open={isBeneficiariesTooltipOpen}
                              className="px-3 py-0"
                            >
                              <div></div>
                            </Tooltip>
                          )}
                          <span
                            className={`flex h-full items-center ${
                              isSidebarShortened ? "hidden" : ""
                            }`}
                          >
                            Beneficiaries
                          </span>
                        </NavLink>
                      </li>
                    </>
                  )}
                {/* POS management belongs to the outlet in view, not to the main
                    wallet, so anyone the matrix grants it sees it there. */}
                {can("pos:manage") && (
                  <li className="h-12 w-full">
                    <NavLink
                      id="pos-management-link"
                      to="#"
                      className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(POS_MANAGEMENT)
                        ? "border-l-primary-300 bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : "bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                      ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                      onClick={(e) => {
                        if (!isRegistrationCompleted) {
                          openFinishRegistrationModal();
                        } else {
                          handleNavLinkClick(
                            e,
                            isRegistrationCompleted
                              ? POS_MANAGEMENT
                              : location.pathname
                          );
                        }
                        onSetShowSideBar(false);
                      }}
                      onMouseEnter={() => setIsPosManagementTooltipOpen(true)}
                      onMouseLeave={() => setIsPosManagementTooltipOpen(false)}
                    >
                      <ClipboardIcon
                        className={` h-full ${
                          isSidebarShortened ? "col-span-1 ml-5.5" : "mr-2 ml-3"
                        }`}
                      />
                      {isSidebarShortened && isTooltip && (
                        <Tooltip
                          {...sidebarTooltipProps}
                          title="POS Management"
                          placement="right"
                          arrow
                          disableInteractive={true}
                          open={isPosManagementTooltipOpen}
                          className="px-3 py-0"
                        >
                          <div></div>
                        </Tooltip>
                      )}
                      <span
                        className={`flex h-full items-center ${
                          isSidebarShortened ? "hidden" : ""
                        }`}
                      >
                        POS Management
                      </span>
                    </NavLink>
                  </li>
                )}
                {/* BOAS-7641: Manager may view and manage the outlets they are
                    linked to; Accountant and Cashier get nothing here. */}
                {IS_OUTLET_MANAGEMENT_ENABLED && can("outlet:read") && (
                  <li className="h-12 w-full">
                    <NavLink
                      id="outlet-management-link"
                      to="#"
                      className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(OUTLET_MANAGEMENT)
                        ? "bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : "bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                      onClick={(e) => {
                        handleNavLinkClick(e, OUTLET_MANAGEMENT);
                        onSetShowSideBar(false);
                      }}
                      onMouseEnter={() =>
                        setIsOutletManagementTooltipOpen(true)
                      }
                      onMouseLeave={() =>
                        setIsOutletManagementTooltipOpen(false)
                      }
                    >
                      <OutletIcon
                        className={`h-full ${
                          isSidebarShortened ? "col-span-1 ml-5.5" : "mr-2 ml-3"
                        }`}
                      />
                      {isSidebarShortened && isTooltip && (
                        <Tooltip
                          {...sidebarTooltipProps}
                          title="Outlet Management"
                          placement="right"
                          arrow
                          disableInteractive={true}
                          open={isOutletManagementTooltipOpen}
                          className="px-3 py-0"
                        >
                          <div></div>
                        </Tooltip>
                      )}
                      <span
                        className={`flex h-full items-center ${
                          isSidebarShortened ? "hidden" : ""
                        }`}
                      >
                        Outlet Management
                      </span>
                    </NavLink>
                  </li>
                )}
                <li className="h-12 w-full">
                  <NavLink
                    id="analytics-link"
                    to="#"
                    className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(ANALYTICS)
                        ? "bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : "bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                    onClick={(e) => {
                      if (!isRegistrationCompleted) {
                        openFinishRegistrationModal();
                      } else {
                        handleNavLinkClick(
                          e,
                          isRegistrationCompleted
                            ? ANALYTICS
                            : location.pathname
                        );
                      }
                      onSetShowSideBar(false);
                    }}
                    onMouseEnter={() => setIsAnalyticsTooltipOpen(true)}
                    onMouseLeave={() => setIsAnalyticsTooltipOpen(false)}
                  >
                    <AnalyticsIcon
                      className={`h-full ${
                        isSidebarShortened ? "col-span-1 ml-5.5" : "mr-2.5 ml-4"
                      }`}
                    />
                    {isSidebarShortened && isTooltip && (
                      <Tooltip
                        {...sidebarTooltipProps}
                        title="Analytics"
                        placement="right"
                        arrow
                        disableInteractive={true}
                        open={isAnalyticsTooltipOpen}
                        className="px-3 py-0"
                      >
                        <div></div>
                      </Tooltip>
                    )}
                    <span
                      className={`flex h-full items-center ${
                        isSidebarShortened ? "hidden" : ""
                      }`}
                    >
                      Analytics
                    </span>
                  </NavLink>
                </li>

                {can("settings:statement") && (
                  <li className="h-12 w-full">
                    <NavLink
                      id="statements-link"
                      to="#"
                      className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(STATEMENTS)
                        ? "bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : " bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                      onClick={(e) => {
                        handleNavLinkClick(e, STATEMENTS);
                        onSetShowSideBar(false);
                      }}
                      onMouseEnter={() => setIsStatementsTooltipOpen(true)}
                      onMouseLeave={() => setIsStatementsTooltipOpen(false)}
                    >
                      <StatementsIcon
                        className={`h-full w-5 ${
                          isSidebarShortened
                            ? "col-span-1 ml-5.5"
                            : "mr-2.5 ml-4"
                        }`}
                      />
                      {isSidebarShortened && isTooltip && (
                        <Tooltip
                          {...sidebarTooltipProps}
                          title="Statements"
                          placement="right"
                          arrow
                          disableInteractive={true}
                          open={isStatementsTooltipOpen}
                          className="px-3 py-0"
                        >
                          <div></div>
                        </Tooltip>
                      )}
                      <span
                        className={`flex h-full items-center ${
                          isSidebarShortened ? "hidden" : ""
                        }`}
                      >
                        Statements
                      </span>
                    </NavLink>
                  </li>
                )}
                {userSettingsPreference?.features?.inventory.enabled &&
                  (isAdmin ||
                    can("inventory:manage") ||
                    can("inventory:stock")) && (
                    <li className="h-12 w-full">
                      <NavLink
                        id="inventory-link"
                        to="#"
                        className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(INVENTORY)
                        ? "bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : " bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                        onClick={(e) => {
                          handleNavLinkClick(e, INVENTORY);
                          onSetShowSideBar(false);
                        }}
                        onMouseEnter={() => setIsInventoryTooltipOpen(true)}
                        onMouseLeave={() => setIsInventoryTooltipOpen(false)}
                      >
                        <InventoryIcon
                          className={`h-full w-5 ${
                            isSidebarShortened
                              ? "col-span-1 ml-5.5"
                              : "mr-2.5 ml-4"
                          } `}
                          fill={
                            location.pathname.includes(INVENTORY)
                              ? "#D13226"
                              : "#fff"
                          }
                        />
                        {isSidebarShortened && isTooltip && (
                          <Tooltip
                            {...sidebarTooltipProps}
                            title="Inventory"
                            placement="right"
                            arrow
                            disableInteractive={true}
                            open={isInventoryTooltipOpen}
                            className="px-3 py-0"
                          >
                            <div></div>
                          </Tooltip>
                        )}
                        <span
                          className={`flex h-full items-center ${
                            isSidebarShortened ? "hidden" : ""
                          }`}
                        >
                          Inventory
                        </span>
                      </NavLink>
                    </li>
                  )}
              </ul>
              <ul className="flex w-full select-none list-none flex-col place-items-start sm:place-items-center">
                <li
                  className={`mb-2 h-12 w-2/3 sm:w-full ${
                    isSidebarShortened ? "mt-1.5" : "mt-20"
                  }`}
                >
                  <NavLink
                    id="settings-link"
                    to="#"
                    className={` flex h-full place-items-center rounded-lg
                    ${
                      location.pathname.includes(SETTINGS)
                        ? "bg-white fill-primary-300 text-primary-300 [&>span]:duration-300"
                        : " bg-neutral-transparent-white fill-white text-white transition duration-200 ease-in-out hover:cursor-pointer hover:bg-primary-200"
                    }
                    ${isSidebarShortened ? "grid grid-cols-3 pl-2" : ""}`}
                    onClick={(e) => {
                      handleNavLinkClick(e, SETTINGS);
                      onSetShowSideBar(false);
                    }}
                    onMouseEnter={() => setIsSettingsTooltipOpen(true)}
                    onMouseLeave={() => setIsSettingsTooltipOpen(false)}
                  >
                    <SettingsIcon
                      className={`h-full w-5 ${
                        isSidebarShortened ? "col-span-1 ml-5.5" : "mr-2.5 ml-4"
                      }`}
                    />
                    {isSidebarShortened && isTooltip && (
                      <Tooltip
                        {...sidebarTooltipProps}
                        title="Settings"
                        placement="right"
                        arrow
                        disableInteractive={true}
                        open={isSettingsTooltipOpen}
                        className="px-3 py-0"
                      >
                        <div></div>
                      </Tooltip>
                    )}
                    <span
                      className={`flex h-full items-center ${
                        isSidebarShortened ? "hidden" : ""
                      }`}
                    >
                      Settings
                    </span>
                  </NavLink>
                </li>
              </ul>
            </aside>
          ) : (
            <props.sidebar
              isSidebarShortened={isSidebarShortened}
              handleNavLinkClick={handleNavLinkClick}
              handleToggleSidebar={handleToggleSidebar}
              sidebarTooltipProps={sidebarTooltipProps}
              location={location}
            />
          )}
        </div>
        <div className="relative">
          <div
            className={`fixed bottom-[95px]  ${
              isSidebarShortened
                ? "translate-x-[56.4px]"
                : "translate-x-[232px]"
            } ${isResized() ? "hidden" : "hidden sm:block"} z-[9999]`}
          >
            <IconButton onClick={handleToggleSidebar}>
              {isSidebarShortened ? (
                <OutlineArrowRight className="rounded-full border border-solid border-gray-200 bg-white fill-primary-300 hover:border-primary-300 hover:bg-pink-100" />
              ) : (
                <OutlineArrowLeft className="rounded-full border border-solid border-gray-200 bg-white fill-primary-300 hover:border-primary-300 hover:bg-pink-100" />
              )}
            </IconButton>
          </div>
        </div>
        <main
          className={`min-h-[calc(100vh-128px)] w-full ${
            isSidebarShortened
              ? "pl-0 sm:pl-24 lg:pl-28"
              : "pl-0 sm:pl-24 lg:pl-72"
          } pr-4 transition-all duration-75 lg:pr-6`}
        >
          <div className="relative w-full max-w-full">
            {isCorporateRestrictedPath && isCorporate ? (
              <Navigate to={DASHBOARD} replace />
            ) : isCorporateRestrictedPath && !isWalletLoaded ? null : (
              <Outlet />
            )}
          </div>
        </main>
      </div>
    </div>
  );
});

export default MainLayout;
